import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faDownload,
    faFileInvoiceDollar,
    faTriangleExclamation,
    faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';

import { useFlashMessage } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';
import { dashboardService } from '../../services/dashboardService';
import { crmService } from '../../services/crmService';
import { fetchHMSEntities } from '../../services/invoicingService';
import {
    getPartnerDeliveriesWithActivity,
    downloadStatementOfAccounts,
} from '../../services/statementService';
import './StatementOfAccountsGenerator.css';

/**
 * Standalone Statement of Accounts generator.
 *
 * Approver-gated form with no movements list (per the SOA spec
 * "does NOT show financial movements in CRM"). Submits the same
 * filters as the CRM Financial tab and downloads the resulting PDF.
 */

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const toIsoDate = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const TIMESPAN_PRESETS = [
    { key: 'this_month', label: 'This month' },
    { key: 'last_month', label: 'Last month' },
    { key: 'ytd', label: 'Year to date' },
    { key: 'last_12_months', label: 'Last 12 months' },
    { key: 'all_time', label: 'All time' },
    { key: 'custom', label: 'Custom range' },
];

const presetToRange = (key) => {
    const today = new Date();
    switch (key) {
        case 'this_month':
            return { date_from: toIsoDate(startOfMonth(today)), date_to: toIsoDate(today) };
        case 'last_month': {
            const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            return {
                date_from: toIsoDate(startOfMonth(prev)),
                date_to: toIsoDate(endOfMonth(prev)),
            };
        }
        case 'ytd':
            return {
                date_from: toIsoDate(new Date(today.getFullYear(), 0, 1)),
                date_to: toIsoDate(today),
            };
        case 'all_time':
            return { date_from: '2000-01-01', date_to: toIsoDate(today) };
        case 'last_12_months':
        default: {
            const from = new Date(today);
            from.setDate(from.getDate() - 365);
            return { date_from: toIsoDate(from), date_to: toIsoDate(today) };
        }
    }
};

const StatementOfAccountsGenerator = () => {
    const history = useHistory();
    const { showError, showSuccess } = useFlashMessage();

    const [authChecked, setAuthChecked] = useState(false);
    const [isApprover, setIsApprover] = useState(false);

    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnerSearch, setPartnerSearch] = useState('');
    const [partnerId, setPartnerId] = useState('');

    const [entities, setEntities] = useState([]);
    // Said beside the picker when the company list fails: an empty picker read
    // as "no companies" while the list refused CRM approvers (ledger row 124).
    const [entitiesError, setEntitiesError] = useState(null);
    const [hmsEntityId, setHmsEntityId] = useState('');

    const [deliveries, setDeliveries] = useState([]);
    const [deliveriesLoading, setDeliveriesLoading] = useState(false);
    const [deliveryId, setDeliveryId] = useState('');

    const [presetKey, setPresetKey] = useState('last_12_months');
    const initialRange = useMemo(() => presetToRange('last_12_months'), []);
    const [customFrom, setCustomFrom] = useState(initialRange.date_from);
    const [customTo, setCustomTo] = useState(initialRange.date_to);

    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState(null);

    // Approver gate. The backend re-checks but the FE hides the form
    // when the user isn't allowed.
    useEffect(() => {
        const check = async () => {
            try {
                const info = await dashboardService.getUserInfo();
                setIsApprover(Boolean(info?.is_crm_approver));
            } catch (err) {
                console.error('SOA generator: getUserInfo failed', err);
                setIsApprover(false);
            } finally {
                setAuthChecked(true);
            }
        };
        check();
    }, []);

    // Load IMT entities once.
    useEffect(() => {
        let cancelled = false;
        const loadEntities = async () => {
            try {
                const data = await fetchHMSEntities();
                if (cancelled) return;
                setEntities(Array.isArray(data) ? data : data?.results || []);
            } catch (err) {
                console.error('SOA generator: failed to load IMT entities', err);
                if (!cancelled) setEntitiesError(getErrorMessage(err, 'The list of companies could not be loaded.'));
            }
        };
        loadEntities();
        return () => {
            cancelled = true;
        };
    }, []);

    // Lazy partner search (debounced) - approved partners only.
    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            if (!isApprover) return;
            setPartnersLoading(true);
            try {
                const data = await crmService.getProfiles({
                    search: partnerSearch.trim() || undefined,
                    limit: 50,
                    offset: 0,
                });
                if (cancelled) return;
                const list = Array.isArray(data) ? data : data?.results || [];
                setPartners(list);
            } catch (err) {
                console.error('SOA generator: failed to load partners', err);
            } finally {
                if (!cancelled) setPartnersLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [partnerSearch, isApprover]);

    // Cascading delivery dropdown - load when partner changes.
    useEffect(() => {
        let cancelled = false;
        const loadDeliveries = async () => {
            if (!partnerId) {
                setDeliveries([]);
                setDeliveryId('');
                return;
            }
            setDeliveriesLoading(true);
            try {
                const payload = await getPartnerDeliveriesWithActivity(partnerId);
                if (cancelled) return;
                setDeliveries(payload?.deliveries || []);
                // Reset delivery filter when partner changes - the previous
                // selection is meaningless for a different partner.
                setDeliveryId('');
            } catch (err) {
                console.error('SOA generator: failed to load deliveries', err);
            } finally {
                if (!cancelled) setDeliveriesLoading(false);
            }
        };
        loadDeliveries();
        return () => {
            cancelled = true;
        };
    }, [partnerId]);

    const handlePresetChange = useCallback((key) => {
        setPresetKey(key);
        if (key !== 'custom') {
            const range = presetToRange(key);
            setCustomFrom(range.date_from);
            setCustomTo(range.date_to);
        }
    }, []);

    const handleSubmit = useCallback(async (e) => {
        if (e) e.preventDefault();
        setError(null);
        if (!partnerId) {
            setError('Please select a partner.');
            return;
        }
        const range = presetKey === 'custom'
            ? { date_from: customFrom, date_to: customTo }
            : presetToRange(presetKey);
        if (!range.date_from || !range.date_to) {
            setError('Please specify both From and To dates.');
            return;
        }
        if (range.date_from > range.date_to) {
            setError('From date cannot be after To date.');
            return;
        }

        setIsDownloading(true);
        try {
            const payload = {
                partner_id: parseInt(partnerId, 10),
                date_from: range.date_from,
                date_to: range.date_to,
            };
            if (deliveryId) payload.delivery_id = parseInt(deliveryId, 10);
            if (hmsEntityId) payload.hms_entity_id = parseInt(hmsEntityId, 10);

            const blob = await downloadStatementOfAccounts(payload);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            const partnerName = partners.find((p) => String(p.id) === String(partnerId))?.legal_entity_name || `partner_${partnerId}`;
            const safeName = partnerName.replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 60);
            a.href = url;
            a.download = `SOA_${safeName}_${range.date_from}_${range.date_to}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            showSuccess('Statement of Accounts generated.');
        } catch (err) {
            console.error('SOA generator: download failed', err);
            const message = getErrorMessage(err, 'Failed to generate Statement of Accounts PDF.');
            setError(message);
            showError(message);
        } finally {
            setIsDownloading(false);
        }
    }, [partnerId, presetKey, customFrom, customTo, deliveryId, hmsEntityId, partners, showError, showSuccess]);

    if (!authChecked) {
        return (
            <div className="soa-screen">
                <div className="soa-card">Loading…</div>
            </div>
        );
    }

    if (!isApprover) {
        return (
            <div className="soa-screen">
                <div className="soa-card soa-denied">
                    <FontAwesomeIcon icon={faShieldHalved} className="soa-denied-icon" />
                    <h2>Approver access required</h2>
                    <p>
                        The Statement of Accounts generator is restricted to CRM Approvers.
                        Please contact your administrator if you need access.
                    </p>
                    <button
                        type="button"
                        className="soa-btn soa-btn-secondary"
                        onClick={() => history.push('/dashboard/crm')}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} /> Back to CRM
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="soa-screen">
            <div className="soa-header">
                <button
                    type="button"
                    className="soa-back-btn"
                    onClick={() => history.push('/dashboard/crm')}
                >
                    <FontAwesomeIcon icon={faArrowLeft} /> Back to CRM
                </button>
                <div className="soa-header-title">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} />
                    <h1>Statement of Accounts</h1>
                </div>
                <div className="soa-header-subtitle">
                    Generate a partner-facing PDF summary of every financial movement in the period.
                    Pro-formas are excluded.
                </div>
            </div>

            <form className="soa-card soa-form" onSubmit={handleSubmit}>
                <div className="soa-field">
                    <label htmlFor="soa-partner-search">Partner search</label>
                    <input
                        id="soa-partner-search"
                        type="text"
                        placeholder="Search partner by legal name…"
                        value={partnerSearch}
                        onChange={(e) => setPartnerSearch(e.target.value)}
                        autoComplete="off"
                    />
                </div>

                <div className="soa-field">
                    <label htmlFor="soa-partner">Partner *</label>
                    <select
                        id="soa-partner"
                        value={partnerId}
                        onChange={(e) => setPartnerId(e.target.value)}
                        required
                    >
                        <option value="">
                            {partnersLoading ? 'Loading partners…' : 'Select a partner'}
                        </option>
                        {partners.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.legal_entity_name}
                                {p.trading_short_name ? ` (${p.trading_short_name})` : ''}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="soa-grid">
                    <div className="soa-field">
                        <label htmlFor="soa-entity">IMT Entity</label>
                        <select
                            id="soa-entity"
                            value={hmsEntityId}
                            onChange={(e) => setHmsEntityId(e.target.value)}
                        >
                            <option value="">All entities</option>
                            {entities.map((entity) => (
                                <option key={entity.id} value={entity.id}>
                                    {entity.hms_entity_name}
                                </option>
                            ))}
                        </select>
                        {entitiesError && (
                            <span role="alert" className="soa-entities-error" style={{ color: '#b91c1c', fontSize: '0.8rem' }}>
                                {entitiesError} The statement can still cover all entities.
                            </span>
                        )}
                    </div>

                    <div className="soa-field">
                        <label htmlFor="soa-delivery">Delivery</label>
                        <select
                            id="soa-delivery"
                            value={deliveryId}
                            onChange={(e) => setDeliveryId(e.target.value)}
                            disabled={!partnerId || deliveriesLoading}
                        >
                            <option value="">
                                {!partnerId
                                    ? 'Select a partner first'
                                    : deliveriesLoading
                                    ? 'Loading deliveries…'
                                    : deliveries.length === 0
                                    ? 'No deliveries with activity'
                                    : 'All deliveries'}
                            </option>
                            {deliveries.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.display_name || d.delivery_unique_number || `Delivery #${d.id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="soa-field">
                    <label htmlFor="soa-preset">Timespan</label>
                    <select
                        id="soa-preset"
                        value={presetKey}
                        onChange={(e) => handlePresetChange(e.target.value)}
                    >
                        {TIMESPAN_PRESETS.map((p) => (
                            <option key={p.key} value={p.key}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                </div>

                {presetKey === 'custom' && (
                    <div className="soa-grid">
                        <div className="soa-field">
                            <label htmlFor="soa-from">From</label>
                            <input
                                id="soa-from"
                                type="date"
                                value={customFrom}
                                onChange={(e) => setCustomFrom(e.target.value)}
                            />
                        </div>
                        <div className="soa-field">
                            <label htmlFor="soa-to">To</label>
                            <input
                                id="soa-to"
                                type="date"
                                value={customTo}
                                onChange={(e) => setCustomTo(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {error && (
                    <div className="soa-error">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        <span>{error}</span>
                    </div>
                )}

                <div className="soa-actions">
                    <button
                        type="submit"
                        className="soa-btn soa-btn-primary"
                        disabled={isDownloading || !partnerId}
                    >
                        <FontAwesomeIcon icon={faDownload} />
                        {isDownloading ? 'Generating…' : 'Generate PDF'}
                    </button>
                </div>

                <div className="soa-note">
                    The Statement of Accounts is a partner-facing summary. It excludes
                    pro-formas and never shows movements in the CRM screen itself.
                </div>
            </form>
        </div>
    );
};

export default StatementOfAccountsGenerator;
